import { Asset } from "@/stores/asset-store";

export type AssetVariant = {
  asset: Asset;
  os: string;
  architecture: string;
  asset_category: string;
  filename?: string; // Include filename for OS detection
};

export type GroupedAsset = {
  asset_name: string;
  display_version: string; // Keep for backward compatibility (latest version)
  display_versions: string[]; // All versions for this asset
  display_release_month: string; // Keep for backward compatibility
  display_release_months: string[]; // Array of all release months for this asset
  asset_description: string;
  size: string;
  variants: AssetVariant[];
  uniqueOS: string[];
};

export type GroupedAssets = {
  "docker & helm": Asset[];
  "helm charts": Asset[];
  "docker images": Asset[];
  dist: {
    agent: Asset[];
    modules: Asset[];
    utilities: Asset[];
  };
};

export type GroupedAssetCounts = {
  "docker & helm": number;
  "helm charts": number;
  "docker images": number;
  dist: {
    agent: number;
    modules: number;
    utilities: number;
  };
};

export function formatAssetVersionLabel(version: string): string {
  const t = version.trim();
  if (!t) {
    return "";
  }
  if (/^v\d/i.test(t)) {
    return t;
  }
  return `v${t}`;
}

/**
 * Groups assets by their top-level asset_group
 */
export function groupAssetsByTopLevel(assets: Asset[]): GroupedAssets {
  const grouped: GroupedAssets = {
    "docker & helm": [],
    "helm charts": [],
    "docker images": [],
    dist: {
      agent: [],
      modules: [],
      utilities: [],
    },
  };

  assets.forEach((asset) => {
    if (asset.asset_group === "helm charts") {
      grouped["docker & helm"].push(asset);
      grouped["helm charts"].push(asset);
    } else if (asset.asset_group === "docker images") {
      grouped["docker & helm"].push(asset);
      grouped["docker images"].push(asset);
    } else if (asset.asset_group === "dist") {
      if (asset.asset_subgroup === "agent") {
        grouped.dist.agent.push(asset);
      } else if (asset.asset_subgroup === "modules") {
        grouped.dist.modules.push(asset);
      } else if (asset.asset_subgroup === "utilities") {
        grouped.dist.utilities.push(asset);
      }
    }
  });

  return grouped;
}

/**
 * Counts unique asset names in each group
 * This gives the count of unique products/versions rather than total release artifacts
 */
export function countUniqueAssetsByTopLevel(assets: Asset[]): GroupedAssetCounts {
  const grouped = groupAssetsByTopLevel(assets);

  // Helper function to count unique asset names
  const countUniqueNames = (assetList: Asset[]): number => {
    const uniqueNames = new Set(assetList.map((asset) => asset.asset_name));
    return uniqueNames.size;
  };

  return {
    "docker & helm": countUniqueNames(grouped["docker & helm"]),
    "helm charts": countUniqueNames(grouped["helm charts"]),
    "docker images": countUniqueNames(grouped["docker images"]),
    dist: {
      agent: countUniqueNames(grouped.dist.agent),
      modules: countUniqueNames(grouped.dist.modules),
      utilities: countUniqueNames(grouped.dist.utilities),
    },
  };
}

export function formatPullCommand(asset: { asset_group: string; pull_command: string }): string {
  if (asset.asset_group === "helm charts") {
    return asset.pull_command.replace(/(oci:\/\/[^/]+)\/[^/]+(\/.*)/, "$1$2");
  }
  if (asset.asset_group === "docker images") {
    return asset.pull_command.replace(/\/remote-docker-chainguard-cache\//, "/customer-docker/");
  }
  return asset.pull_command;
}

/**
 * Formats an asset field value, returning "N/A" for empty values
 */
export function formatAssetField(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  return String(value);
}

/**
 * Returns display label for asset group
 */
export function getGroupLabel(group: string): string {
  const labels: Record<string, string> = {
    "docker & helm": "Docker & Helm",
    dist: "Dist",
    agent: "Istari Agent",
    modules: "Integrations",
    utilities: "Utilities",
  };
  return labels[group] || group;
}

/**
 * Formats a list of items for display, showing up to maxVisible items with overflow indicator
 */
export function formatListWithOverflow(items: string[], maxVisible: number = 3): string {
  if (items.length <= maxVisible) {
    return items.join(", ");
  }

  const visible = items.slice(0, maxVisible);
  const remaining = items.length - maxVisible;
  return `${visible.join(", ")} ,${remaining} more`;
}

/**
 * Detects if a version string is calendar-based (YYYY.MM format)
 */
function isCalendarVersion(version: string): boolean {
  // Calendar versions are typically YYYY.MM or YYYY.MM.DD format
  // Check if it starts with 20XX (year) and has at most 3 parts
  const parts = version.split(".");
  if (parts.length > 3) return false;

  const firstPart = parseInt(parts[0], 10);
  // If first part is a year (2000-2099), it's likely a calendar version
  return firstPart >= 2000 && firstPart < 2100;
}

/** Chainguard-style rebuild suffix: `<version>-<YYYYMMDDHHMM>` (UTC build time, minute precision). */
const BUILD_TIMESTAMP_SUFFIX = /^(.*)-(\d{12})$/;

/**
 * Splits a tag into its version core and rebuild timestamp.
 * "1.56.2-202610201234" → { core: "1.56.2", timestamp: "202610201234" }; "1.56.2" → { core: "1.56.2", timestamp: "" }
 */
function splitBuildTimestamp(version: string): { core: string; timestamp: string } {
  const match = BUILD_TIMESTAMP_SUFFIX.exec(version);
  return match ? { core: match[1], timestamp: match[2] } : { core: version, timestamp: "" };
}

/** Numeric dot-separated parts of a semantic version; a leading "v" is not significant, non-numeric parts count as 0. */
function semanticParts(core: string): number[] {
  return core
    .replace(/^v(?=\d)/, "")
    .split(".")
    .map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
}

/**
 * Compares two version strings intelligently
 * Automatically detects calendar vs semantic versioning and uses appropriate comparison.
 * Semantic versions compare numerically on their core; on an equal core, a rebuild timestamp
 * (`-YYYYMMDDHHMM`) orders newer than no timestamp, and later timestamps order newer.
 * Returns: negative if a < b, positive if a > b, 0 if equal
 */
export function compareVersions(a: string, b: string): number {
  // Handle empty/unknown versions
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  // Check if both are calendar versions
  const aIsCalendar = isCalendarVersion(a);
  const bIsCalendar = isCalendarVersion(b);

  // If both are calendar versions, use string comparison (YYYY.MM sorts correctly)
  if (aIsCalendar && bIsCalendar) {
    return a.localeCompare(b);
  }

  // If one is calendar and one is semantic, calendar comes first (newer)
  if (aIsCalendar && !bIsCalendar) return 1;
  if (!aIsCalendar && bIsCalendar) return -1;

  // Both are semantic versions - compare the numeric core first
  const aVersion = splitBuildTimestamp(a);
  const bVersion = splitBuildTimestamp(b);
  const aParts = semanticParts(aVersion.core);
  const bParts = semanticParts(bVersion.core);

  const maxLength = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < maxLength; i++) {
    const aNum = aParts[i] || 0;
    const bNum = bParts[i] || 0;

    if (aNum !== bNum) {
      return aNum - bNum;
    }
  }

  // Same core: the rebuild timestamp decides ("" sorts oldest; equal-length digit strings compare as numbers)
  if (aVersion.timestamp === bVersion.timestamp) return 0;
  return aVersion.timestamp < bVersion.timestamp ? -1 : 1;
}

/**
 * Groups assets by their asset name (not version)
 * Collects all versions and release months for each asset
 * Used to consolidate multiple OS/architecture/version variants into single rows
 */
export function groupAssetsByNameVersion(assets: Asset[]): GroupedAsset[] {
  const grouped = new Map<string, GroupedAsset>();

  assets.forEach((asset) => {
    const key = asset.asset_name; // Group by name only

    // Initialize group if it doesn't exist
    if (!grouped.has(key)) {
      grouped.set(key, {
        asset_name: asset.asset_name,
        display_version: asset.display_version,
        display_versions: [asset.display_version],
        display_release_month: asset.display_release_month,
        display_release_months: [asset.display_release_month],
        asset_description: asset.asset_description,
        size: asset.size, // Use size from first variant
        variants: [],
        uniqueOS: [],
      });
    }

    const group = grouped.get(key)!;

    // Add version if not already present
    if (asset.display_version && !group.display_versions.includes(asset.display_version)) {
      group.display_versions.push(asset.display_version);
    }

    // Add release month if not already present
    if (asset.display_release_month && !group.display_release_months.includes(asset.display_release_month)) {
      group.display_release_months.push(asset.display_release_month);
    }

    // Update to latest version using intelligent version comparison
    if (compareVersions(asset.display_version, group.display_version) > 0) {
      group.display_version = asset.display_version;
    }

    group.variants.push({
      asset,
      os: asset.operating_system,
      architecture: asset.architecture,
      asset_category: asset.asset_category,
      filename: asset.download_name, // Include filename for OS detection
    });

    // Add OS to uniqueOS list if not already present
    if (!group.uniqueOS.includes(asset.operating_system)) {
      group.uniqueOS.push(asset.operating_system);
    }
  });

  // Sort versions and release months for each group
  grouped.forEach((group) => {
    // Sort versions in descending order (newest first) using intelligent version comparison
    group.display_versions.sort((a, b) => compareVersions(b, a));

    // Sort release months in descending order (newest first)
    group.display_release_months.sort((a, b) => b.localeCompare(a));

    // Sort variants consistently: first by version (desc), then by OS, then by architecture
    group.variants.sort((a, b) => {
      // First sort by version (descending - newest first) using intelligent comparison
      const versionCompare = compareVersions(b.asset.display_version, a.asset.display_version);
      if (versionCompare !== 0) return versionCompare;
      // Then sort by OS
      const osCompare = a.os.localeCompare(b.os);
      if (osCompare !== 0) return osCompare;
      // Then sort by architecture
      return a.architecture.localeCompare(b.architecture);
    });
  });

  return Array.from(grouped.values());
}

/**
 * Filters assets based on search query
 */
export function filterAssets(assets: Asset[], query: string): Asset[] {
  if (!query) return assets;

  const lowerQuery = query.toLowerCase();
  return assets.filter((asset) => {
    return (
      asset.asset_name.toLowerCase().includes(lowerQuery) ||
      asset.download_name.toLowerCase().includes(lowerQuery) ||
      asset.version.toLowerCase().includes(lowerQuery) ||
      asset.asset_description.toLowerCase().includes(lowerQuery)
    );
  });
}

export type AssetCategory = {
  key: "docker & helm" | "agent" | "modules" | "utilities";
  group: string;
  subgroup: string | null;
  count: (counts: GroupedAssetCounts) => number;
};

/**
 * The asset categories surfaced in the sidebar and on the dashboard
 */
export const ASSET_CATEGORIES: AssetCategory[] = [
  { key: "docker & helm", group: "docker & helm", subgroup: null, count: (counts) => counts["docker & helm"] },
  { key: "agent", group: "dist", subgroup: "agent", count: (counts) => counts.dist.agent },
  { key: "modules", group: "dist", subgroup: "modules", count: (counts) => counts.dist.modules },
  { key: "utilities", group: "dist", subgroup: "utilities", count: (counts) => counts.dist.utilities },
];

/**
 * Builds the assets listing URL filtered to one category
 */
export function assetCategoryPath(category: AssetCategory): string {
  const params = new URLSearchParams({ group: category.group });
  if (category.subgroup) {
    params.set("subgroup", category.subgroup);
  }
  return `/assets?${params.toString()}`;
}
